import * as React from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

type KeywordType = 'primary' | 'secondary' | 'custom';

type DashboardSummary = {
    tracked_keywords: number;
    keywords_with_rank: number;
    current_average_rank: number | null;
    rank_delta_7d: number | null;
    rank_delta_30d: number | null;
    top_3_count: number;
    top_10_count: number;
    impressions: number;
    clicks: number;
    ctr: number;
    average_gsc_position: number | null;
};

type DashboardTimeseriesRow = {
    snapshot_date: string;
    average_rank: number | null;
    impressions: number;
    clicks: number;
    ctr: number;
};

type DashboardKeywordRow = {
    keyword_id: number;
    keyword_text: string;
    keyword_type: KeywordType;
    latest_rank: number | null;
    rank_delta_7d: number | null;
    rank_delta_30d: number | null;
    impressions: number;
    clicks: number;
    ctr: number;
    avg_position: number | null;
    last_seen_date: string | null;
};

type DashboardResponse = {
    summary: DashboardSummary;
    timeseries: DashboardTimeseriesRow[];
    keywords: DashboardKeywordRow[];
    sync_status?: {
        latest_rank_snapshot_date: string | null;
        latest_gsc_snapshot_date: string | null;
    } | null;
};

const getApiBase = (): string => {
    const root = String(process.env.REACT_APP_API_URL || '').trim();
    if (!root) return '/api';
    if (root.endsWith('/api')) return root;
    if (root.endsWith('/')) return `${root}api`;
    return `${root}/api`;
};

const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('user_authToken');
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
};

const formatNumber = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return Number(value).toFixed(digits);
};

const formatInteger = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return `${Math.round(Number(value))}`;
};

const formatSigned = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    const shown = Number(value);
    const sign = shown > 0 ? '+' : '';
    return `${sign}${shown.toFixed(2)}`;
};

const rangeOptions = [
    { label: '7 Days', value: 7 },
    { label: '14 Days', value: 14 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
];

type KeywordPerformancePanelProps = {
    productId?: number | null;
    title?: string;
};

const KeywordPerformancePanel: React.FC<KeywordPerformancePanelProps> = ({
    productId,
    title = 'SEO Keyword Performance',
}) => {
    const apiBase = React.useMemo(() => getApiBase(), []);

    const [rangeDays, setRangeDays] = React.useState<number>(14);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [syncing, setSyncing] = React.useState<boolean>(false);
    const [savingKeyword, setSavingKeyword] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');
    const [dashboard, setDashboard] = React.useState<DashboardResponse | null>(null);
    const [newKeyword, setNewKeyword] = React.useState<string>('');
    const [newKeywordType, setNewKeywordType] = React.useState<KeywordType>('custom');

    const loadDashboard = React.useCallback(async () => {
        if (!productId) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get<DashboardResponse>(
                `${apiBase}/seo-keywords/products/${productId}/dashboard`,
                {
                    params: { range_days: rangeDays },
                    headers: getAuthHeaders(),
                }
            );
            setDashboard(response.data);
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to load SEO dashboard';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [apiBase, productId, rangeDays]);

    React.useEffect(() => {
        void loadDashboard();
    }, [loadDashboard]);

    const handleSyncNow = async () => {
        if (!productId) return;
        setSyncing(true);
        setError('');
        try {
            await axios.post(
                `${apiBase}/seo-keywords/products/${productId}/sync`,
                {},
                { headers: getAuthHeaders() }
            );
            await loadDashboard();
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to sync keyword metrics';
            setError(message);
        } finally {
            setSyncing(false);
        }
    };

    const handleAddKeyword = async () => {
        const keywordText = newKeyword.trim();
        if (!keywordText || !productId) return;
        setSavingKeyword(true);
        setError('');
        try {
            await axios.post(
                `${apiBase}/seo-keywords/products/${productId}/keywords`,
                {
                    keyword_text: keywordText,
                    keyword_type: newKeywordType,
                },
                { headers: getAuthHeaders() }
            );
            setNewKeyword('');
            await loadDashboard();
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to save keyword';
            setError(message);
        } finally {
            setSavingKeyword(false);
        }
    };

    const handleDeactivateKeyword = async (keywordId: number) => {
        if (!productId) return;
        setError('');
        try {
            await axios.patch(
                `${apiBase}/seo-keywords/products/${productId}/keywords/${keywordId}`,
                { is_active: false },
                { headers: getAuthHeaders() }
            );
            await loadDashboard();
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to deactivate keyword';
            setError(message);
        }
    };

    if (!productId) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <Select
                        size="small"
                        value={rangeDays}
                        onChange={(event) => setRangeDays(Number(event.target.value))}
                    >
                        {rangeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button onClick={() => void loadDashboard()} variant="outlined" size="small">
                        Refresh
                    </Button>
                    <Button
                        onClick={() => void handleSyncNow()}
                        variant="contained"
                        size="small"
                        disabled={syncing}
                    >
                        {syncing ? 'Syncing...' : 'Sync Now'}
                    </Button>
                </Stack>
            </Stack>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {loading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : null}

            {!loading && dashboard ? (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Tracked Keywords</Typography>
                                <Typography variant="h6">{formatInteger(dashboard.summary.tracked_keywords)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Avg Rank</Typography>
                                <Typography variant="h6">{formatNumber(dashboard.summary.current_average_rank)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    7d: {formatSigned(dashboard.summary.rank_delta_7d)}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Top 10 Keywords</Typography>
                                <Typography variant="h6">{formatInteger(dashboard.summary.top_10_count)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Top 3: {formatInteger(dashboard.summary.top_3_count)}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Impressions</Typography>
                                <Typography variant="h6">{formatInteger(dashboard.summary.impressions)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Clicks / CTR</Typography>
                                <Typography variant="h6">{formatInteger(dashboard.summary.clicks)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    CTR: {formatNumber(dashboard.summary.ctr)}%
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Add keyword"
                            value={newKeyword}
                            onChange={(event) => setNewKeyword(event.target.value)}
                        />
                        <Select
                            size="small"
                            value={newKeywordType}
                            onChange={(event) => setNewKeywordType(event.target.value as KeywordType)}
                        >
                            <MenuItem value="primary">Primary</MenuItem>
                            <MenuItem value="secondary">Secondary</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                        <Button
                            variant="outlined"
                            onClick={() => void handleAddKeyword()}
                            disabled={savingKeyword || !newKeyword.trim()}
                        >
                            {savingKeyword ? 'Saving...' : 'Add'}
                        </Button>
                    </Stack>

                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Keyword Metrics</Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Keyword</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Latest Rank</TableCell>
                                <TableCell>7d Delta</TableCell>
                                <TableCell>Impressions</TableCell>
                                <TableCell>Clicks</TableCell>
                                <TableCell>CTR</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dashboard.keywords.map((row) => (
                                <TableRow key={row.keyword_id}>
                                    <TableCell>{row.keyword_text}</TableCell>
                                    <TableCell>{row.keyword_type}</TableCell>
                                    <TableCell>{formatNumber(row.latest_rank)}</TableCell>
                                    <TableCell>{formatSigned(row.rank_delta_7d)}</TableCell>
                                    <TableCell>{formatInteger(row.impressions)}</TableCell>
                                    <TableCell>{formatInteger(row.clicks)}</TableCell>
                                    <TableCell>{formatNumber(row.ctr)}%</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="warning"
                                            onClick={() => void handleDeactivateKeyword(row.keyword_id)}
                                        >
                                            Disable
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Daily Trend</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Avg Rank</TableCell>
                                <TableCell>Impressions</TableCell>
                                <TableCell>Clicks</TableCell>
                                <TableCell>CTR</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dashboard.timeseries.map((row) => (
                                <TableRow key={row.snapshot_date}>
                                    <TableCell>{row.snapshot_date}</TableCell>
                                    <TableCell>{formatNumber(row.average_rank)}</TableCell>
                                    <TableCell>{formatInteger(row.impressions)}</TableCell>
                                    <TableCell>{formatInteger(row.clicks)}</TableCell>
                                    <TableCell>{formatNumber(row.ctr)}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Last rank sync: {dashboard.sync_status?.latest_rank_snapshot_date || '--'} | Last GSC sync: {dashboard.sync_status?.latest_gsc_snapshot_date || '--'}
                    </Typography>
                </>
            ) : null}
        </Paper>
    );
};

export default KeywordPerformancePanel;
